import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getApiUrl, API_CONFIG } from "../config/api";
import ReviewCard from "../components/ReviewCard";
import {
  Review,
  OzonReviewsResponse,
  WildberriesReviewsResponse,
} from "../types";
import {
  useWindowVirtualizer,
  measureElement as rvMeasureElement,
} from "@tanstack/react-virtual";
import { useReviewAnalysis } from "../hooks/useReviewAnalysis";
import { useReviewResponse } from "../hooks/useReviewResponse";
import { useExternalReviews } from "../hooks/useExternalReviews";
import { parseDateToMs } from "../utils/date";
import {
  mapOzonToShared,
  mapWildberriesToShared,
  createPlaceholderProduct,
} from "../utils/reviewMapping";
import "../styles/newReviews.css";
import axios from "axios";

// moved to utils/date

// cursors moved into useExternalReviews hook

// page sizing handled in hook

// moved to utils/reviewMapping

// moved to utils/reviewMapping

// moved to utils/reviewMapping

const Reviews: React.FC = () => {
  // Data moved to useExternalReviews hook
  const {
    reviews,
    loading,
    hasMore,
    error: apiError,
    fetchNextPage,
    refresh,
    updateReviews,
  } = useExternalReviews();
  // Track new items by key to style newly arrived reviews
  const [newKeys, setNewKeys] = useState<Set<string>>(() => new Set());
  const newKeyTimeoutsRef = useRef<Record<string, number>>({});
  const [currentPage, setCurrentPage] = useState(1);

  // cursors and store lists are managed inside useExternalReviews hook
  // per-platform cursors moved to useExternalReviews hook
  const initializedRef = useRef<boolean>(false);
  const fetchReviewsRef = useRef<((page?: number) => Promise<void>) | null>(
    null
  );

  // Переключатель "Только с текстом"
  const [onlyWithText, setOnlyWithText] = useState<boolean>(() => {
    const saved = localStorage.getItem("onlyWithText");
    return saved !== null ? JSON.parse(saved) : false;
  });
  const handleOnlyWithTextChange = useCallback((value: boolean) => {
    setOnlyWithText(value);
    localStorage.setItem("onlyWithText", JSON.stringify(value));
  }, []);

  // Виртуализация списка (используем отфильтрованный набор только для отображения)
  const rows = React.useMemo(
    () =>
      onlyWithText
        ? reviews.filter((r: Review) => r.text && r.text.trim().length > 0)
        : reviews,
    [reviews, onlyWithText]
  );
  const virtualizer = useWindowVirtualizer({
    count: rows.length + (hasMore ? 1 : 0),
    estimateSize: () => 300,
    overscan: 6,
    measureElement: rvMeasureElement,
    getItemKey: (index) =>
      rows[index]
        ? `${(rows[index] as any).platform}:${(rows[index] as any).id}`
        : index,
  });
  const virtualItems = virtualizer.getVirtualItems();
  const lastVirtualItem = virtualItems[virtualItems.length - 1];
  const lastIndex = lastVirtualItem ? lastVirtualItem.index : -1;
  const prevLastIndexRef = useRef<number | null>(null);

  // При смене режима отображения (только с текстом/все) сбрасываем реф курсора прокрутки,
  // чтобы механизм автоподгрузки мог заново среагировать на достижение конца списка
  useEffect(() => {
    prevLastIndexRef.current = null;
  }, [onlyWithText]);

  // Хуки анализа/ответа
  const { analyzingReviews, handleAnalyzeReview } = useReviewAnalysis();
  const {
    generatingResponses,
    handleGenerateResponse,
    handleRegenerateResponse,
  } = useReviewResponse();

  const checkApiSettings = useCallback(() => {
    // В мульти-магазинном режиме полагаемся на подключенные магазины,
    // поэтому не блокируем загрузку по полям пользователя.
    // Сообщения об ошибках будут показаны по факту реальных ошибок загрузки.
    return true;
  }, []);

  const authHeaders = useMemo(() => {
    const token = localStorage.getItem("token");
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    } as Record<string, string>;
  }, []);

  const getReviewKey = useCallback(
    (r: Review) => `${r.platform}:${r.id}`,
    [
      /* no deps */
    ]
  );

  const fetchLatestSnapshot = useCallback(async (): Promise<Review[]> => {
    // Загружаем подключённые магазины, чтобы дергать отзывы по каждому магазину отдельно
    const storesUrl = `${getApiUrl("/api/stores")}`;
    const { data: stores } = await axios.get<any[]>(storesUrl, {
      headers: authHeaders,
    });

    const ozonStores = (stores || []).filter(
      (s) => s.platform === "ozon" && s.isActive !== false
    );
    const wbStores = (stores || []).filter(
      (s) => s.platform === "wildberries" && s.isActive !== false
    );
    const yaStores = (stores || []).filter(
      (s) => s.platform === "yandex" && s.isActive !== false
    );

    const ozonUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.OZON_REVIEWS)}`;
    const wbUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS)}`;
    const yaUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS)}`;

    // По каждому магазину — свой запрос и добавляем storeId/storeName в отзывы
    const ozonLists = await Promise.all(
      ozonStores.map(async (s: any) => {
        const resp = await axios.get<OzonReviewsResponse>(ozonUrl, {
          params: { limit: 21, storeId: s.id },
          headers: authHeaders,
        });
        const list = (resp.data?.reviews || [])
          .map((r: any) => {
            const mapped = mapOzonToShared(r) as Review;
            mapped.storeId = s.id;
            mapped.storeName = s.name;
            return mapped;
          })
          .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
        return list;
      })
    );

    const wbLists = await Promise.all(
      wbStores.map(async (s: any) => {
        const resp = await axios.get<WildberriesReviewsResponse>(wbUrl, {
          params: {
            take: 21,
            skip: 0,
            order: "dateDesc",
            storeId: s.id,
          },
          headers: authHeaders,
        });
        const feedbacks = (resp.data as any)?.data?.feedbacks || [];
        const list = feedbacks
          .map((f: any) => {
            const mapped = mapWildberriesToShared(f) as Review;
            mapped.storeId = s.id;
            mapped.storeName = s.name;
            return mapped;
          })
          .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
        return list;
      })
    );

    const yaLists = await Promise.all(
      yaStores.map(async (s: any) => {
        const resp = await axios.get<any>(yaUrl, {
          params: { limit: 21, storeId: s.id },
          headers: authHeaders,
        });
        const feedbacks = (resp.data?.reviews || []) as any[];
        const list = feedbacks
          .map((f: any) => {
            const sellerAnswerText =
              f?.answer !== undefined && f?.answer !== null
                ? f.answer
                : (f?.sellerAnswer ?? null);
            const isAnswered =
              typeof sellerAnswerText === "string"
                ? sellerAnswerText.trim().length > 0
                : Boolean(sellerAnswerText);
            const mapped: Review = {
              comments_amount: 0,
              id: String(
                f.feedbackId ??
                  f.id ??
                  `${s.id}:${f.createdAt || Math.random()}`
              ),
              isAnalyzed: Boolean(f.isAnalyzed) || false,
              isAnsweredBySeller: (f as any).isAnsweredBySeller ?? isAnswered,
              isFallback: Boolean(f.isFallback) || false,
              isResponded:
                (f as any).isResponded ?? (isAnswered ? true : false),
              is_rating_participant: false,
              offer_id: String(f.identifiers?.modelId ?? ""),
              order_status: "",
              photos_amount: Array.isArray(f.media?.photos)
                ? f.media.photos.length
                : 0,
              platform: "yandex" as any,
              problems: f.problems ?? null,
              product: createPlaceholderProduct({
                id: String(f.identifiers?.modelId ?? ""),
              }),
              published_at: f.createdAt,
              rating: Number(f.statistics?.rating ?? 0),
              recommendations: f.recommendations ?? null,
              responseTemplate: f.responseTemplate ?? null,
              sellerAnswer: (sellerAnswerText as any) ?? null,
              sentiment: f.sentiment ?? null,
              sentimentAnalysis: f.sentimentAnalysis ?? null,
              sku: Number(f.identifiers?.modelId ?? 0) || 0,
              status: "",
              text: [f.description?.advantages, f.description?.comment]
                .filter(Boolean)
                .join(" \n"),
              videos_amount: Array.isArray(f.media?.videos)
                ? f.media.videos.length
                : 0,
            } as Review;
            (mapped as any).storeId = s.id;
            (mapped as any).storeName = s.name;
            // Имя автора (для Yandex): кладём на верхний уровень, чтобы карточка могла отобразить
            (mapped as any).author =
              (f && (f.author || f.authorName || f.userName)) || "";
            (mapped as any).yandex = {
              advantages: f.description?.advantages || "",
              disadvantages: f.description?.disadvantages || "",
              comment: f.description?.comment || "",
              author: (f && (f.author || f.authorName || f.userName)) || "",
            };
            return mapped;
          })
          .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
        return list;
      })
    );

    const merged = ([] as Review[]).concat(
      ...ozonLists,
      ...wbLists,
      ...yaLists
    );
    merged.sort(
      (a, b) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    return merged;
  }, [authHeaders]);

  const prependNew = useCallback(
    (incoming: Review[]) => {
      if (incoming.length === 0) return;
      updateReviews((prev: Review[]) => {
        const existingKeys = new Set(prev.map(getReviewKey));
        const prevMaxMs = prev.length
          ? Math.max(...prev.map((r: Review) => parseDateToMs(r.published_at)))
          : -Infinity;
        // Только действительно новые по времени и отсутствующие в списке
        const fresh = incoming
          .filter((r: Review) => !existingKeys.has(getReviewKey(r)))
          .filter((r: Review) => parseDateToMs(r.published_at) > prevMaxMs);
        if (fresh.length === 0) return prev;
        fresh.sort(
          (a: Review, b: Review) =>
            parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
        );
        // mark new keys and schedule removal
        setNewKeys((old) => {
          const next = new Set(old);
          for (const r of fresh as Review[]) {
            const k = getReviewKey(r);
            next.add(k);
            if (newKeyTimeoutsRef.current[k]) {
              window.clearTimeout(newKeyTimeoutsRef.current[k]);
            }
            newKeyTimeoutsRef.current[k] = window.setTimeout(() => {
              setNewKeys((cur) => {
                const copy = new Set(cur);
                copy.delete(k);
                return copy;
              });
              delete newKeyTimeoutsRef.current[k];
            }, 12000);
          }
          return next;
        });
        return [...fresh, ...prev];
      });
    },
    [getReviewKey, updateReviews]
  );

  useEffect(() => {
    const interval = window.setInterval(async () => {
      try {
        const latest = await fetchLatestSnapshot();
        prependNew(latest);
      } catch (_) {
        // ignore polling errors
      }
    }, 120000);
    return () => window.clearInterval(interval);
  }, [fetchLatestSnapshot, prependNew]);

  /* moved into useExternalReviews hook
  const fetchOzonPage = useCallback(async () => {
    // Инициализация магазинов и курсоров
    if (
      storesRef.current.ozon.length === 0 &&
      storesRef.current.wb.length === 0
    ) {
      const storesUrl = `${getApiUrl("/api/stores")}`;
      const { data: stores } = await axios.get<any[]>(storesUrl, {
        headers: authHeaders,
      });
      const oz = (stores || [])
        .filter((s: any) => s.platform === "ozon" && s.isActive !== false)
        .map((s: any) => ({ id: s.id, name: s.name }));
      const wb = (stores || [])
        .filter(
          (s: any) => s.platform === "wildberries" && s.isActive !== false
        )
        .map((s: any) => ({ id: s.id, name: s.name }));
      const ya = (stores || [])
        .filter((s: any) => s.platform === "yandex" && s.isActive !== false)
        .map((s: any) => ({ id: s.id, name: s.name }));
      storesRef.current = { ozon: oz, wb, ya };
      for (const s of oz) {
        if (!ozonStoreStateRef.current[s.id])
          ozonStoreStateRef.current[s.id] = { lastId: null, hasNext: true };
      }
      for (const s of wb) {
        if (!wbStoreStateRef.current[s.id])
          wbStoreStateRef.current[s.id] = {
            skip: 0,
            take: PLATFORM_PAGE_SIZE,
            hasMore: true,
          };
      }
      for (const s of ya) {
        if (!yaStoreStateRef.current[s.id])
          yaStoreStateRef.current[s.id] = { dateTimeTo: null, hasMore: true };
      }
    }

    const url = `${getApiUrl(API_CONFIG.ENDPOINTS.OZON_REVIEWS)}`;
    const combined: Review[] = [];
    let anyHasNext = false;
    for (const s of storesRef.current.ozon) {
      const st = ozonStoreStateRef.current[s.id] || {
        lastId: null,
        hasNext: true,
      };
      if (!st.hasNext) continue;
      const params: any = { limit: PLATFORM_PAGE_SIZE, storeId: s.id };
      if (st.lastId) params.last_id = st.lastId;
      const { data } = await axios.get<OzonReviewsResponse>(url, {
        params,
        headers: authHeaders,
      });
      const list = data.reviews || [];
      const mapped = list
        .map((r: any) => {
          const m = mapOzonToShared(r) as Review;
          m.storeId = s.id;
          m.storeName = s.name;
          return m;
        })
        .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
      combined.push(...mapped);
      ozonStoreStateRef.current[s.id] = {
        lastId: (data as any).last_id ?? st.lastId,
        hasNext: Boolean((data as any).has_next),
      };
      anyHasNext = anyHasNext || Boolean((data as any).has_next);
    }
    combined.sort(
      (a, b) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    ozonRef.current.hasNext = anyHasNext;
    return combined;
  }, [authHeaders]);
  */

  /* moved into useExternalReviews hook
  const fetchWildberriesPage = useCallback(async () => {
    // Инициализация магазинов и курсоров
    if (
      storesRef.current.wb.length === 0 &&
      storesRef.current.ozon.length === 0
    ) {
      const storesUrl = `${getApiUrl("/api/stores")}`;
      const { data: stores } = await axios.get<any[]>(storesUrl, {
        headers: authHeaders,
      });
      const oz = (stores || [])
        .filter((s: any) => s.platform === "ozon" && s.isActive !== false)
        .map((s: any) => ({ id: s.id, name: s.name }));
      const wb = (stores || [])
        .filter(
          (s: any) => s.platform === "wildberries" && s.isActive !== false
        )
        .map((s: any) => ({ id: s.id, name: s.name }));
      const ya = (stores || [])
        .filter((s: any) => s.platform === "yandex" && s.isActive !== false)
        .map((s: any) => ({ id: s.id, name: s.name }));
      storesRef.current = { ozon: oz, wb, ya };
      for (const s of oz) {
        if (!ozonStoreStateRef.current[s.id])
          ozonStoreStateRef.current[s.id] = { lastId: null, hasNext: true };
      }
      for (const s of wb) {
        if (!wbStoreStateRef.current[s.id])
          wbStoreStateRef.current[s.id] = {
            skip: 0,
            take: PLATFORM_PAGE_SIZE,
            hasMore: true,
          };
      }
      for (const s of ya) {
        if (!yaStoreStateRef.current[s.id])
          yaStoreStateRef.current[s.id] = { dateTimeTo: null, hasMore: true };
      }
    }

    const url = `${getApiUrl(API_CONFIG.ENDPOINTS.WILDBERRIES_REVIEWS)}`;
    const combined: Review[] = [];
    let anyHasMore = false;
    for (const s of storesRef.current.wb) {
      const st = wbStoreStateRef.current[s.id] || {
        skip: 0,
        take: PLATFORM_PAGE_SIZE,
        hasMore: true,
      };
      if (!st.hasMore) continue;
      const params: any = {
        take: st.take,
        skip: st.skip,
        order: "dateDesc",
        storeId: s.id,
      };
      const { data } = await axios.get<WildberriesReviewsResponse>(url, {
        params,
        headers: authHeaders,
      });
      const feedbacks = (data as any)?.data?.feedbacks || [];
      const mapped = feedbacks
        .map((f: any) => {
          const m = mapWildberriesToShared(f) as Review;
          m.storeId = s.id;
          m.storeName = s.name;
          return m;
        })
        .filter((r: Review) => parseDateToMs(r.published_at) !== -Infinity);
      combined.push(...mapped);
      // ВАЖНО: skip не увеличиваем здесь. Увеличим позже только
      // на количество реально показанных отзывов WB в итоговой странице.
      wbStoreStateRef.current[s.id] = {
        skip: st.skip,
        take: st.take,
        hasMore: feedbacks.length >= st.take,
      };
      anyHasMore = anyHasMore || feedbacks.length >= st.take;
    }
    combined.sort(
      (a, b) => parseDateToMs(b.published_at) - parseDateToMs(a.published_at)
    );
    wbRef.current.hasMore = anyHasMore;
    wbRef.current.lastFetched = combined;
    return combined;
  }, [authHeaders]);
  */

  /* moved into useExternalReviews hook
  const mergeNextPage = useCallback(async (): Promise<{ items: Review[]; more: boolean } | null> => {
    await ensureBuffers();

    const pageItems: Review[] = [];
    // Счетчик, сколько WB-отзывов реально вошло в текущую страницу по каждому магазину
    const wbShownByStore: Record<string, number> = {};
    const yaShownByStore: Record<string, number> = {};
    while (pageItems.length < PAGE_SIZE) {
      const ozonHead = ozonRef.current.buffer[0];
      const wbHead = wbRef.current.buffer[0];
      // Получаем головные элементы Yandex (по одному из каждого магазина)
      // Загружаем при необходимости
      if (storesRef.current.ya.length > 0) {
        for (const s of storesRef.current.ya) {
          const st = yaStoreStateRef.current[s.id] || {
            dateTimeTo: null,
            hasMore: true,
          };
          // Буфер для Yandex будем хранить во временном поле на объекте состояния
          const key = `__ya_buffer_${s.id}` as const;
          const anyGlobal = yaStoreStateRef as any;
          if (!anyGlobal[key] || (anyGlobal[key] as Review[]).length === 0) {
            if (!st.hasMore) continue;
            const yaUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS)}`;
            const params: any = { limit: PLATFORM_PAGE_SIZE, storeId: s.id };
            if (st.dateTimeTo) params.dateTimeTo = st.dateTimeTo;
            try {
              const resp = await axios.get<any>(yaUrl, {
                params,
                headers: authHeaders,
              });
              const feedbacks = (resp.data?.reviews || []) as any[];
              const mapped = feedbacks
                .map((f: any) => {
                  const sellerAnswerText =
                    (f?.answer !== undefined && f?.answer !== null)
                      ? f.answer
                      : (f?.sellerAnswer ?? null);
                  const isAnswered =
                    typeof sellerAnswerText === "string"
                      ? sellerAnswerText.trim().length > 0
                      : Boolean(sellerAnswerText);
                  const mapped: Review = {
                    comments_amount: 0,
                    id: String(
                      f.feedbackId ??
                        f.id ??
                        `${s.id}:${f.createdAt || Math.random()}`
                    ),
                    isAnalyzed: Boolean(f.isAnalyzed) || false,
                    isAnsweredBySeller:
                      (f as any).isAnsweredBySeller ?? isAnswered,
                    isFallback: Boolean(f.isFallback) || false,
                    isResponded:
                      (f as any).isResponded ?? (isAnswered ? true : false),
                    is_rating_participant: false,
                    offer_id: String(f.identifiers?.modelId ?? ""),
                    order_status: "",
                    photos_amount: Array.isArray(f.media?.photos)
                      ? f.media.photos.length
                      : 0,
                    platform: "yandex" as any,
                    problems: f.problems ?? null,
                    product: createPlaceholderProduct({
                      id: String(f.identifiers?.modelId ?? ""),
                    }),
                    published_at: f.createdAt,
                    rating: Number(f.statistics?.rating ?? 0),
                    recommendations: f.recommendations ?? null,
                    responseTemplate: f.responseTemplate ?? null,
                    sellerAnswer: (sellerAnswerText as any) ?? null,
                    sentiment: f.sentiment ?? null,
                    sentimentAnalysis: f.sentimentAnalysis ?? null,
                    sku: Number(f.identifiers?.modelId ?? 0) || 0,
                    status: "",
                    text: [f.description?.advantages, f.description?.comment]
                      .filter(Boolean)
                      .join(" \n"),
                    videos_amount: Array.isArray(f.media?.videos)
                      ? f.media.videos.length
                      : 0,
                  } as Review;
                  (mapped as any).storeId = s.id;
                  (mapped as any).storeName = s.name;
                  (mapped as any).author =
                    (f && (f.author || f.authorName || f.userName)) || "";
                  (mapped as any).yandex = {
                    advantages: f.description?.advantages || "",
                    disadvantages: f.description?.disadvantages || "",
                    comment: f.description?.comment || "",
                    author:
                      (f && (f.author || f.authorName || f.userName)) || "",
                  };
                  return mapped;
                })
                .filter(
                  (r: Review) => parseDateToMs(r.published_at) !== -Infinity
                );
              anyGlobal[key] = mapped;
              if (mapped.length < PLATFORM_PAGE_SIZE) {
                yaStoreStateRef.current[s.id] = {
                  ...st,
                  hasMore: false,
                };
              }
            } catch (_) {
              anyGlobal[key] = [];
              yaStoreStateRef.current[s.id] = { ...st, hasMore: false };
            }
          }
        }
      }

      // Собираем один общий head для Yandex из буферов всех магазинов
      const yaHeads: Array<{ storeId: string; review: Review }> = [];
      for (const s of storesRef.current.ya) {
        const key = `__ya_buffer_${s.id}` as const;
        const buf: Review[] = (yaStoreStateRef as any)[key] || [];
        if (buf[0]) yaHeads.push({ storeId: s.id, review: buf[0] });
      }

      const yaHead = yaHeads.sort(
        (a, b) =>
          parseDateToMs(b.review.published_at) -
          parseDateToMs(a.review.published_at)
      )[0]?.review;

      if (!ozonHead && !wbHead && !yaHead) {
        const fetchedAny: boolean[] = [false, false, false];
        if (ozonRef.current.hasNext) {
          const moreO = await fetchOzonPage();
          ozonRef.current.buffer = moreO;
          fetchedAny[0] = moreO.length > 0;
        }
        if (wbRef.current.hasMore) {
          const moreW = await fetchWildberriesPage();
          wbRef.current.buffer = moreW;
          fetchedAny[1] = moreW.length > 0;
        }
        // Для Yandex: если у всех магазинов буферы пусты, пробуем явно подгрузить по dateTimeTo
        let anyYaFetched = false;
        for (const s of storesRef.current.ya) {
          const key = `__ya_buffer_${s.id}` as const;
          const buf: Review[] = (yaStoreStateRef as any)[key] || [];
          const st = yaStoreStateRef.current[s.id] || {
            dateTimeTo: null,
            hasMore: true,
          };
          if (buf.length === 0 && st.hasMore) {
            const yaUrl = `${getApiUrl(API_CONFIG.ENDPOINTS.YANDEX_REVIEWS)}`;
            const params: any = { limit: PLATFORM_PAGE_SIZE, storeId: s.id };
            if (st.dateTimeTo) params.dateTimeTo = st.dateTimeTo;
            try {
              const resp = await axios.get<any>(yaUrl, {
                params,
                headers: authHeaders,
              });
              const feedbacks = (resp.data?.reviews || []) as any[];
              const mapped = feedbacks
                .map((f: any) => {
                  const sellerAnswerText =
                    (f?.answer !== undefined && f?.answer !== null)
                      ? f.answer
                      : (f?.sellerAnswer ?? null);
                  const isAnswered =
                    typeof sellerAnswerText === "string"
                      ? sellerAnswerText.trim().length > 0
                      : Boolean(sellerAnswerText);
                  const mapped: Review = {
                    comments_amount: 0,
                    id: String(
                      f.feedbackId ??
                        f.id ??
                        `${s.id}:${f.createdAt || Math.random()}`
                    ),
                    isAnalyzed: Boolean(f.isAnalyzed) || false,
                    isAnsweredBySeller:
                      (f as any).isAnsweredBySeller ?? isAnswered,
                    isFallback: Boolean(f.isFallback) || false,
                    isResponded:
                      (f as any).isResponded ?? (isAnswered ? true : false),
                    is_rating_participant: false,
                    offer_id: String(f.identifiers?.modelId ?? ""),
                    order_status: "",
                    photos_amount: Array.isArray(f.media?.photos)
                      ? f.media.photos.length
                      : 0,
                    platform: "yandex" as any,
                    problems: f.problems ?? null,
                    product: createPlaceholderProduct({
                      id: String(f.identifiers?.modelId ?? ""),
                    }),
                    published_at: f.createdAt,
                    rating: Number(f.statistics?.rating ?? 0),
                    recommendations: f.recommendations ?? null,
                    responseTemplate: f.responseTemplate ?? null,
                    sellerAnswer: (sellerAnswerText as any) ?? null,
                    sentiment: f.sentiment ?? null,
                    sentimentAnalysis: f.sentimentAnalysis ?? null,
                    sku: Number(f.identifiers?.modelId ?? 0) || 0,
                    status: "",
                    text: [f.description?.advantages, f.description?.comment]
                      .filter(Boolean)
                      .join(" \n"),
                    videos_amount: Array.isArray(f.media?.videos)
                      ? f.media.videos.length
                      : 0,
                  } as Review;
                  (mapped as any).storeId = s.id;
                  (mapped as any).storeName = s.name;
                  (mapped as any).yandex = {
                    advantages: f.description?.advantages || "",
                    disadvantages: f.description?.disadvantages || "",
                    comment: f.description?.comment || "",
                  };
                  return mapped;
                })
                .filter(
                  (r: Review) => parseDateToMs(r.published_at) !== -Infinity
                );
              (yaStoreStateRef as any)[key] = mapped;
              if (mapped.length < PLATFORM_PAGE_SIZE) {
                yaStoreStateRef.current[s.id] = {
                  ...st,
                  hasMore: false,
                };
              }
              anyYaFetched = anyYaFetched || mapped.length > 0;
            } catch (_) {
              (yaStoreStateRef as any)[key] = [];
              yaStoreStateRef.current[s.id] = { ...st, hasMore: false };
            }
          }
        }
        fetchedAny[2] = anyYaFetched;
        if (!fetchedAny[0] && !fetchedAny[1]) break;
        continue;
      }

      const ozonTime = ozonHead
        ? parseDateToMs(ozonHead.published_at)
        : -Infinity;
      const wbTime = wbHead ? parseDateToMs(wbHead.published_at) : -Infinity;
      const yaTime = yaHead ? parseDateToMs(yaHead.published_at) : -Infinity;
      if (ozonTime >= wbTime && ozonTime >= yaTime && ozonHead) {
        pageItems.push(ozonRef.current.buffer.shift() as Review);
      } else if (wbTime >= ozonTime && wbTime >= yaTime && wbHead) {
        const taken = wbRef.current.buffer.shift() as Review;
        pageItems.push(taken);
        if (taken.storeId)
          wbShownByStore[taken.storeId] =
            (wbShownByStore[taken.storeId] || 0) + 1;
      } else if (yaHead) {
        // Снимаем head из соответствующего буфера магазина Yandex
        for (const s of storesRef.current.ya) {
          const key = `__ya_buffer_${s.id}` as const;
          const buf: Review[] = (yaStoreStateRef as any)[key] || [];
          if (buf[0] && buf[0].id === yaHead.id) {
            const taken = (yaStoreStateRef as any)[key].shift() as Review;
            pageItems.push(taken);
            if (taken.storeId)
              yaShownByStore[taken.storeId] =
                (yaShownByStore[taken.storeId] || 0) + 1;
            break;
          }
        }
      }
    }

    // Проверка края страницы: набираем 20, но платформы грузим по 21 и проверяем последнюю границу
    const finalizeWithBoundaryCheck = (items: Review[]): Review[] => {
      if (items.length === 0) return items;
      // Последний выбранный
      const last = items[items.length - 1];
      const lastPlatform = last.platform;
      const boundaryTime = parseDateToMs(last.published_at);

      // Функция поиска следующего кандидата в другом буфере после последнего выбранного из соответствующей платформы
      const findNextCandidateTimeForPlatform = (
        platform: string
      ): number | null => {
        if (platform === "wildberries") {
          // Найдем последний выбранный из ozon
          for (let i = items.length - 1; i >= 0; i -= 1) {
            if (items[i].platform === "ozon") {
              // В буфере ozonRef текущий head — более свежие. Мы хотим следующий за этим id среди уже загруженных (включая текущий буфер)
              // Для простоты: берем первый элемент из текущего буфера ozon как ближайший следующий кандидат (т.к. мы выбирали head-ы по дате)
              const candidate = ozonRef.current.buffer[0];
              if (candidate) return parseDateToMs(candidate.published_at);
              return null;
            }
          }
          return null;
        } else {
          // platform === 'ozon' → ищем последний выбранный из wildberries
          for (let i = items.length - 1; i >= 0; i -= 1) {
            if (items[i].platform === "wildberries") {
              const candidate = wbRef.current.buffer[0];
              if (candidate) return parseDateToMs(candidate.published_at);
              return null;
            }
          }
          return null;
        }
      };

      const nextOtherTime = findNextCandidateTimeForPlatform(lastPlatform);
      if (nextOtherTime !== null && nextOtherTime > boundaryTime) {
        // Удаляем последний элемент и возвращаем 19 (по ТЗ)
        return items.slice(0, items.length - 1);
      }
      return items;
    };

    const checked = finalizeWithBoundaryCheck(pageItems);
    // boundary adjustment no longer affects per-store cursor state

    // Обновляем курсоры следующей страницы на основе последних отобранных

    // Обновляем per-store курсоры: берём последний отобранный по каждому магазину OZON и увеличиваем skip для WB-магазинов
    const lastPerOzonStore: Record<string, string> = {};
    for (let i = checked.length - 1; i >= 0; i -= 1) {
      const r = checked[i];
      if (r.platform === "ozon" && r.storeId && !lastPerOzonStore[r.storeId]) {
        lastPerOzonStore[r.storeId] = r.id;
      }
    }
    for (const storeId of Object.keys(lastPerOzonStore)) {
      if (ozonStoreStateRef.current[storeId]) {
        ozonStoreStateRef.current[storeId].lastId = lastPerOzonStore[storeId];
      }
    }
    // Для WB: корректно увеличиваем skip только на реально показанные WB-отзывы
    for (const [storeId, shownCount] of Object.entries(wbShownByStore)) {
      if (wbStoreStateRef.current[storeId]) {
        wbStoreStateRef.current[storeId].skip += shownCount;
      }
    }

    // Для Yandex: вычисляем dateTimeTo как createdAt последнего отзыва Yandex на этой странице по каждому магазину
    const lastPerYaStore: Record<string, string> = {};
    for (let i = checked.length - 1; i >= 0; i -= 1) {
      const r = checked[i];
      if (r.platform === "yandex" && r.storeId && !lastPerYaStore[r.storeId]) {
        // createdAt приходит как ISO в поле published_at
        lastPerYaStore[r.storeId] = String(r.published_at);
      }
    }
    for (const [storeId, dt] of Object.entries(lastPerYaStore)) {
      if (yaStoreStateRef.current[storeId]) {
        yaStoreStateRef.current[storeId].dateTimeTo = dt || null;
      }
    }

    // ВАЖНО: очищаем буферы, чтобы следующая страница всегда загружалась НОВЫМИ данными по курсорам,
    // а не использовала оставшиеся элементы из предыдущих загрузок
    ozonRef.current.buffer = [];
    wbRef.current.buffer = [];
    // Для Yandex — очищаем пер-магазинные буферы, чтобы следующая страница использовала dateTimeTo
    for (const s of storesRef.current.ya) {
      const key = `__ya_buffer_${s.id}` as const;
      (yaStoreStateRef as any)[key] = [];
    }

    const anyYaHasMore = storesRef.current.ya.some(
      (s) => yaStoreStateRef.current[s.id]?.hasMore !== false
    );
    const more =
      ozonRef.current.hasNext || wbRef.current.hasMore || anyYaHasMore;

    if (checked.length === 0 && !more) return null;
    return { items: checked, more };
  }, [ensureBuffers, fetchOzonPage, fetchWildberriesPage, authHeaders]);
  */

  const fetchReviews = useCallback(
    async (page: number = 1) => {
      if (!checkApiSettings()) return;
      if (page === 1) {
        await refresh();
      } else {
        await fetchNextPage();
      }
      setCurrentPage(page);
    },
    [checkApiSettings, fetchNextPage, refresh]
  );

  useEffect(() => {
    if (initializedRef.current) return;
    initializedRef.current = true;
    setCurrentPage(1);
    fetchReviews(1);
  }, [fetchReviews]);

  useEffect(() => {
    fetchReviewsRef.current = fetchReviews;
  }, [fetchReviews]);

  useEffect(() => {
    if (lastIndex === -1) return;
    if (prevLastIndexRef.current === null) {
      prevLastIndexRef.current = lastIndex;
      return;
    }
    const hasReachedEnd = lastIndex >= rows.length;
    const scrolledFurther = lastIndex > prevLastIndexRef.current;
    if (
      hasMore &&
      hasReachedEnd &&
      scrolledFurther &&
      !loading &&
      fetchReviewsRef.current
    ) {
      fetchReviewsRef.current(currentPage + 1);
    }
    prevLastIndexRef.current = lastIndex;
  }, [lastIndex, rows.length, hasMore, loading, currentPage]);

  // AI статус теперь в шапке (AiStatusProvider). Убираем локальный вызов.

  const [showAnalysis, setShowAnalysis] = useState<boolean>(() => {
    const saved = localStorage.getItem("showAnalysis");
    return saved !== null ? JSON.parse(saved) : true;
  });

  const handleShowAnalysisChange = useCallback((value: boolean) => {
    setShowAnalysis(value);
    localStorage.setItem("showAnalysis", JSON.stringify(value));
  }, []);

  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;
      setShowScrollToTop(scrollTop > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const onAnalyzeReview = useCallback(
    async (reviewId: string, platform: "ozon" | "wildberries" | "yandex") => {
      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        updateReviews((prevReviews: Review[]) => {
          const next =
            typeof updater === "function"
              ? (updater as any)(prevReviews)
              : updater;
          return next as Review[];
        });
      };
      const ok = await handleAnalyzeReview(
        reviewId,
        setSharedReviews,
        platform
      );
      if (ok) {
        handleShowAnalysisChange(true);
      }
      return ok;
    },
    [handleAnalyzeReview, handleShowAnalysisChange, updateReviews]
  );

  const onGenerateResponse = useCallback(
    (reviewId: string, platform: "ozon" | "wildberries" | "yandex") => {
      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        updateReviews((prevReviews: Review[]) => {
          const next =
            typeof updater === "function"
              ? (updater as any)(prevReviews)
              : updater;
          return next as Review[];
        });
      };
      handleGenerateResponse(reviewId, setSharedReviews, platform);
    },
    [handleGenerateResponse, updateReviews]
  );

  const onRegenerateResponse = useCallback(
    (reviewId: string, platform: "ozon" | "wildberries" | "yandex") => {
      const setSharedReviews = (updater: React.SetStateAction<Review[]>) => {
        updateReviews((prevReviews: Review[]) => {
          const next =
            typeof updater === "function"
              ? (updater as any)(prevReviews)
              : updater;
          return next as Review[];
        });
      };
      handleRegenerateResponse(reviewId, setSharedReviews, platform);
    },
    [handleRegenerateResponse, updateReviews]
  );

  return (
    <div className="space-y-6">
      {/* Убрали фиксированный прелоадер в правом верхнем углу */}
      <div className="pt-2 px-2 sm:px-4 lg:px-6">
        <div className="flex flex-col space-y-6">
          <div className="flex justify-between items-center w-full">
            <div className="flex items-center justify-between space-x-6 w-full">
              <div className="flex items-center space-x-4">
                <div className="p-3 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-200 shadow-sm">
                  <svg
                    className="w-8 h-8 text-blue-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-3xl font-extrabold tracking-tight bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                    Отзывы
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Управление и анализ отзывов клиентов
                  </p>
                </div>
              </div>
              <div className="flex items-center justify-end space-x-3">
                <label className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 h-11">
                  <span className="text-sm font-medium text-gray-700">
                    Показать анализ
                  </span>
                  <div className="relative inline-block w-10 h-6">
                    <input
                      type="checkbox"
                      checked={showAnalysis}
                      onChange={(e) =>
                        handleShowAnalysisChange(e.target.checked)
                      }
                      className="sr-only"
                    />
                    <div
                      className={`absolute inset-0 w-10 h-6 rounded-full transition-colors duration-200 ${showAnalysis ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${showAnalysis ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </div>
                  </div>
                </label>

                <label className="flex items-center space-x-3 px-4 py-3 bg-gradient-to-r from-gray-50 to-slate-50 rounded-xl border border-gray-200 shadow-sm cursor-pointer hover:bg-gray-100 transition-colors duration-200 h-11">
                  <span className="text-sm font-medium text-gray-700">
                    Только с текстом
                  </span>
                  <div className="relative inline-block w-10 h-6">
                    <input
                      type="checkbox"
                      checked={onlyWithText}
                      onChange={(e) =>
                        handleOnlyWithTextChange(e.target.checked)
                      }
                      className="sr-only"
                    />
                    <div
                      className={`absolute inset-0 w-10 h-6 rounded-full transition-colors duration-200 ${onlyWithText ? "bg-blue-600" : "bg-gray-300"}`}
                    >
                      <div
                        className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transform transition-transform duration-200 ${onlyWithText ? "translate-x-4" : "translate-x-0"}`}
                      />
                    </div>
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {apiError && (
          <div className="mt-4 mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-800">{apiError}</p>
              </div>
            </div>
          </div>
        )}

        {loading && reviews.length === 0 ? (
          <div className="flex justify-center items-center min-h-[240px]">
            <div className="flex flex-col items-center gap-3">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600"
                aria-label="Загрузка"
              />
              <p className="text-sm text-gray-600">Загрузка отзывов…</p>
            </div>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-16">
            <div className="inline-flex flex-col items-center space-y-4 p-8 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl border border-gray-200 shadow-sm">
              <div className="p-4 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full">
                <svg
                  className="w-8 h-8 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <div>
                <p className="text-lg font-semibold text-gray-700">
                  Отзывов с текстом не найдено
                </p>
                <p className="text-sm text-gray-500 mt-1">
                  Попробуйте изменить параметры поиска
                </p>
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-6 px-0 sm:px-0 lg:px-0">
            <div
              className="relative"
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: "100%",
              }}
            >
              {virtualItems.map((vi) => {
                const isLoaderRow = vi.index >= rows.length;
                const review = rows[vi.index];
                return (
                  <div
                    key={vi.key}
                    className="absolute left-0 right-0 px-0"
                    style={{
                      top: 0,
                      transform: `translateY(${vi.start}px)`,
                      height: `${vi.size}px`,
                    }}
                  >
                    <div
                      ref={virtualizer.measureElement}
                      data-index={vi.index}
                      className={`box-border ${vi.index < rows.length - 1 && vi.index < rows.length ? "pb-5" : "pb-0"}`}
                    >
                      {isLoaderRow ? (
                        <div className="flex justify-center items-center py-6">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-blue-600" />
                        </div>
                      ) : (
                        <div className="relative">
                          <ReviewCard
                            review={review}
                            isNew={newKeys.has(
                              `${review.platform}:${review.id}`
                            )}
                            analyzingReviews={analyzingReviews}
                            generatingResponses={generatingResponses}
                            onAnalyzeReview={onAnalyzeReview}
                            onGenerateResponse={onGenerateResponse}
                            onRegenerateResponse={onRegenerateResponse}
                            showAnalysis={showAnalysis}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {!hasMore && reviews.length > 0 && (
              <div className="text-center py-6">
                <div className="inline-flex items-center space-x-3 px-6 py-3 bg-gradient-to-r from-green-50 to-emerald-50 rounded-full border border-green-200">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm font-medium text-green-700">
                    Все отзывы с текстом загружены
                  </span>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-500 ease-in-out ${showScrollToTop ? "opacity-100 translate-y-0 scale-100" : "opacity-0 translate-y-4 scale-95 pointer-events-none"}`}
      >
        <button
          onClick={scrollToTop}
          className="group relative p-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-110 hover:-translate-y-1 focus:outline-none focus:ring-4 focus:ring-blue-500/30 focus:ring-offset-2"
          aria-label="Прокрутить наверх"
        >
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
          <svg
            className="relative w-6 h-6 transform transition-transform duration-300 group-hover:-translate-y-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 10l7-7m0 0l7 7m-7-7v18"
            />
          </svg>
          <div className="absolute bottom-full right-0 mb-2 px-3 py-1 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap">
            Наверх
            <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
          </div>
        </button>
      </div>
    </div>
  );
};

export default Reviews;
